import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

import { ModalOpenPDF } from "./components/modal-open-pdf";
import type { ModalOpenPDFProps } from "./components/modal-open-pdf.lib";

export type OpenPdfViewerOptions = Omit<ModalOpenPDFProps, "isOpen" | "setIsOpen" | "openModal" | "closeModal">;

export type PdfViewerApi = {
  open: (options: OpenPdfViewerOptions) => void;
  close: () => void;
};

const PdfViewerContext = createContext<PdfViewerApi | null>(null);

export const PdfViewerProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<OpenPdfViewerOptions | null>(null);

  const open = useCallback((next: OpenPdfViewerOptions) => {
    setOptions(next);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const api = useMemo<PdfViewerApi>(() => ({ open, close }), [close, open]);

  return (
    <PdfViewerContext.Provider value={api}>
      {children}
      {options ? <ModalOpenPDF {...options} isOpen={isOpen} setIsOpen={setIsOpen} openModal={() => setIsOpen(true)} closeModal={close} /> : null}
    </PdfViewerContext.Provider>
  );
};

export const usePdfViewer = (): PdfViewerApi => {
  const api = useContext(PdfViewerContext);
  if (!api) {
    throw new Error("usePdfViewer must be used within PdfViewerProvider");
  }
  return api;
};
